import { customFetch, invalidateCache } from '@/utils/fetch'
import toast from 'react-hot-toast'
import { nanoid } from '@/lib/nanoid'
import { create } from 'zustand'
import { ChatAPI } from '@/api-client/chat'

export const TimeWindow = {
  THIRTY_DAYS: '30d',
  NINETY_DAYS: '90d',
  PAST_YEAR: '1y',
  ALL_TIME: 'all-time',
} as const

export type TimeWindow = (typeof TimeWindow)[keyof typeof TimeWindow]

export type ChatThreadFrontend = {
  id: string
  name: string
  product_id: string
  workspace_id: string
  created_by: string
  created_at: number
}

type ChatStore = {
  isModalOpen: boolean
  setIsModalOpen: (isModalOpen: boolean) => void

  threadId: string | null
  setThreadId: (threadId: string) => void

  isEmptyThread: boolean
  setIsEmptyThread: (isEmptyThread: boolean) => void

  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void

  error: string | null

  timeWindow: TimeWindow
  setTimeWindow: (timeWindow: TimeWindow) => void

  products: Product[]
  setProducts: (products: Product[]) => void
  selectedProduct: Product | null
  setSelectedProduct: (product: Product) => void

  input: string
  setInput: (input: string) => void

  threads: ChatThreadFrontend[]
  setThreads: (threads: ChatThreadFrontend[]) => void

  initData: (threadId: string) => void

  refreshThreads: () => void

  newThread: () => void

  switchThread: (threadId: string) => void

  addToNewChatThread: ({ productId, name, message }: { productId: string; name: string; message: string }) => void
}

export type Product = {
  id: string
  name: string
  workspace_id: string
  logo: string
  platform: string
}

const SELECTED_PRODUCT_LOCAL_STORAGE_KEY = 'selectedProduct'

const getStoredSelectedProduct = () => {
  const selectedProduct = localStorage.getItem(SELECTED_PRODUCT_LOCAL_STORAGE_KEY)
  return selectedProduct ? JSON.parse(selectedProduct) : null
}

const setStoredSelectedProduct = (product: Product) => {
  localStorage.setItem(SELECTED_PRODUCT_LOCAL_STORAGE_KEY, JSON.stringify(product))
}

export const useChatStore = create<ChatStore>((set, get) => ({
  isModalOpen: false,
  setIsModalOpen: (isModalOpen: boolean) => set({ isModalOpen }),

  threadId: null,
  setThreadId: (threadId: string) => set({ threadId }),

  isEmptyThread: false,
  setIsEmptyThread: (isEmptyThread: boolean) => set({ isEmptyThread }),

  isLoading: false,
  setIsLoading: (isLoading: boolean) => set({ isLoading }),
  error: null,

  timeWindow: TimeWindow.THIRTY_DAYS,
  setTimeWindow: (timeWindow: TimeWindow) => set({ timeWindow }),
  products: [],
  setProducts: (products: Product[]) => set({ products }),
  selectedProduct: getStoredSelectedProduct(),
  setSelectedProduct: (product: Product) => {
    set({ selectedProduct: product })
    setStoredSelectedProduct(product)
    get().refreshThreads()
  },

  input: '',
  setInput: (input: string) => set({ input }),

  threads: [],
  setThreads: (threads: ChatThreadFrontend[]) => set({ threads }),

  initData: async (threadId?: string) => {
    set({ isLoading: true })
    try {
      const { products, error } = await customFetch('/getProducts', 'GET')

      // Always handle potential errors to avoid leaving the UI in a perpetual loading state
      if (error) {
        // If the user is unauthenticated, surface a clear message so the component can render the error UI
        if (error.status === 401) {
          set({ isLoading: false, error: 'Unauthenticated – please sign in again.' })
          return
        }

        toast.error('Error fetching products')
        console.error('Error fetching products from chatStore', error)
        set({ isLoading: false, error: 'Error fetching products' })
        return
      }

      console.log('-- chatStore initData', threadId)

      products.sort((a, b) => a.order - b.order)

      set({ products })

      let selectedProductId: string | null = null

      if (threadId) {
        try {
          const { chatThread } = await customFetch(`/chat/threads/getChatThread/${threadId}`, 'GET')
          if (chatThread && chatThread.exists) {
            const product = products.find((product) => product.id === chatThread.data.product_id)
            if (product) {
              set({ selectedProduct: product })
              selectedProductId = product.id
            }
          } else {
            const selectedProduct = products[0]
            if (selectedProduct) {
              set({ selectedProduct })
              selectedProductId = selectedProduct.id
            }
          }
        } catch (error) {
          // If there's an error fetching the thread, fall back to first product
          console.error('Error fetching thread:', error)
          const selectedProduct = products[0]
          set({ selectedProduct })
          selectedProductId = selectedProduct.id
        }
      } else {
        const selectedProduct = products[0]
        set({ selectedProduct })
        selectedProductId = selectedProduct.id
      }

      if (selectedProductId) {
        try {
          const { chatThreads } = await customFetch(`/chat/threads/getChatThreads/${selectedProductId}`, 'GET')
          if (Array.isArray(chatThreads)) {
            set({ threads: chatThreads, isLoading: false })
          }
        } catch (error) {
          console.error('Error fetching threads:', error)
          set({ threads: [], isLoading: false })
        }
      } else {
        set({ threads: [], isLoading: false })
      }
    } catch (error) {
      console.error('Error in initData:', error)
      set({ isLoading: false, error: 'Failed to initialize data' })
    }
  },

  refreshThreads: async () => {
    const { selectedProduct } = get()

    if (!selectedProduct) {
      return
    }

    const endpoint = `/chat/threads/getChatThreads/${selectedProduct.id}`

    invalidateCache(endpoint, 'GET')
    const { chatThreads } = await customFetch(endpoint, 'GET')

    set({ threads: chatThreads })
  },

  newThread: () => {
    set({ isEmptyThread: true, threadId: null })
  },

  switchThread: (threadId: string) => {
    set({ threadId, isEmptyThread: false })
  },

  /**
   * Creates a new thread with the message and adds it to the chat to quickly start a new conversation
   */
  addToNewChatThread: async ({ productId, name, message }: { productId: string; name: string; message: string }) => {
    try {
      const threadId = nanoid()
      const { chatThread } = await customFetch('/chat/threads/createChatThread', 'POST', {
        productId,
        threadId,
        name,
      })

      localStorage.setItem('newChatInput', message)

      // Navigate to the new thread
      get().switchThread(chatThread.id)

      get().refreshThreads()

      // Asynchronously get and update the title suggestion
      ChatAPI.suggestTitle(chatThread.id, message)
        .then(() => {
          // Refresh threads to get the updated title
          get().refreshThreads()
        })
        .catch((error) => {
          console.error('Error getting title suggestion:', error)
          // Don't show error to user since this is a non-critical enhancement
        })
    } catch (error) {
      console.error('Error creating chat thread:', error)
      // If something fails, fall back to the simple navigation
      set({ threadId: null, isEmptyThread: true })
    }
  },
}))
